import React from 'react';
import { BadgeSection } from './BadgeSection';
import { useBusinessesByBadge } from '@/hooks/useBusinessesByBadge';
import { useTranslation } from 'react-i18next';
import { GroupedBusinessResponse } from '@communitly/shared';
import { LucideIcon } from 'lucide-react';
import { useFeaturedGroupingParameter } from '@/hooks/useParameters';

interface FeaturedCategorySectionsProps {
  currentCommunity: any;
  prepareServiceForCard: (service: any) => any;
  getBadgeIcon: (iconName: string) => React.ComponentType<any>;
  getBadgeDisplayName: (badgeSlug: string) => string;
  badges: any[];
}

export const FeaturedCategorySections: React.FC<FeaturedCategorySectionsProps> = ({
  currentCommunity,
  prepareServiceForCard,
  getBadgeIcon,
  getBadgeDisplayName,
  badges
}) => {
  const { t } = useTranslation();
  const { groupByCategory } = useFeaturedGroupingParameter();
  
  const { data: groupedBusinesses, isLoading } = useBusinessesByBadge({
    badgeSlug: 'featured',
    communityId: currentCommunity?.id || '',
    limit: 10, // Increased limit to accommodate multiple categories
    enabled: !!currentCommunity?.id,
    groupByCategory: groupByCategory
  });

  const featuredBadge = badges?.find(b => b.slug === 'featured');
  const Icon = getBadgeIcon('Sparkles') as LucideIcon;

  if (isLoading) {
    return (
      <section className="py-3 lg:py-12 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-48 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Array(3).fill(0).map((_, index) => (
                <div key={index} className="bg-card rounded-2xl overflow-hidden shadow-sm border border-border">
                  <div className="h-48 bg-muted"></div>
                  <div className="p-5 space-y-3">
                    <div className="h-5 bg-muted rounded w-3/4"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                    <div className="h-4 bg-muted rounded w-1/4"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!groupedBusinesses) {
    return null; // No featured businesses
  }

  // Handle single group mode (groupByCategory = false)
  if (!groupByCategory) {
    // When not grouping by category, groupedBusinesses will be a flat array
    const businesses = Array.isArray(groupedBusinesses) ? groupedBusinesses : [];
    
    if (businesses.length === 0) {
      return null;
    }

    return (
      <BadgeSection
        key="featured-single"
        title={t('home.featured.title')}
        icon={Icon}
        services={businesses}
        loading={false}
        emptyMessage={t('home.services.no.featured')}
        prepareServiceForCard={prepareServiceForCard}
        badgeColor={featuredBadge?.color}
      />
    );
  }

  // Handle category-based grouping mode (groupByCategory = true)
  if (typeof groupedBusinesses !== 'object' || Object.keys(groupedBusinesses).length === 0) {
    return null;
  }

  // Get category groups and filter out empty ones
  const categoryGroups = Object.entries(groupedBusinesses)
    .filter(([_, group]) => group.businesses && group.businesses.length > 0)
    .sort(([a], [b]) => a.localeCompare(b)); // Sort alphabetically by category key

  if (categoryGroups.length === 0) {
    return null;
  }

  return (
    <>
      {categoryGroups.map(([categoryKey, group]) => {
        // Create a section title for each category (without "Featured" prefix)
        const sectionTitle = categoryKey === 'default' 
          ? t('home.featured.title') 
          : group.category_name;

        return (
          <BadgeSection
            key={`featured-${categoryKey}`}
            title={sectionTitle}
            icon={Icon}
            services={group.businesses || []}
            loading={false}
            emptyMessage={t('home.services.no.featured.category', { 
              category: group.category_name,
              defaultValue: `No businesses in ${group.category_name}`
            })}
            prepareServiceForCard={prepareServiceForCard}
            badgeColor={featuredBadge?.color}
          />
        );
      })}
    </>
  );
};